import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EditableTable } from '../../EditableTable';
import mainAxios from '../../../services/mainAxios';
import { toast } from 'react-toastify';
import { useAppContext } from '../../../AppContext';
import { useLocation, useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faEdit } from '@fortawesome/free-solid-svg-icons';
import { unlinkProvider } from './KeycloackProvider';

const generateRandomString = (length) => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let result = '';
  const randomValues = new Uint8Array(length);
  crypto.getRandomValues(randomValues);
  for (let i = 0; i < length; i++) {
    result += characters.charAt(randomValues[i] % characters.length);
  }
  return result;
};

const generateCodeChallenge = async (codeVerifier) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

const AuthenticationProviders = ({ state, getCurrentUser }) => {
  const {
    user: [loggedInUser],
    selectedCompany: [selectedCompany],
  } = useAppContext();
  const { t } = useTranslation("translations");
  const location = useLocation();

  const [microsoftCodeVerifier, setMicrosoftCodeVerifier] = useState(null);
  const [microsoftNonce, setMicrosoftNonce] = useState(null);

  useEffect(() => {
    const generateMicrosoftPKCE = async () => {
      const verifier = generateRandomString(64); 
      const challenge = await generateCodeChallenge(verifier);
      const nonce = generateRandomString(32);
      setMicrosoftCodeVerifier({ verifier, challenge });
      setMicrosoftNonce(nonce);
    };
    generateMicrosoftPKCE();
  }, []);

  const providerDisabled = loggedInUser?.role?.id ==1 && state?.user?.role?.id!=1

  const columns = [
    {
      name: "Provider Name",
      selector: (cell) => cell?.provider?.name ?? "",
      sortable: true,
      editable: false,
    },
    {
      name: "Email",
      selector: "username",
      editable: true,
    },
  ];

  const providers = [
    { id: 1, name: "Google" },
    { id: 3, name: "Keycloak" },
    { id: 4, name: "Microsoft" },
  ];

  const tableData = useMemo(() => {
    if (state?.user?.userProviders) {
      return providers.map((provider, index) => {
        let temp = null;
        let exists = state?.user?.userProviders?.find((currentProvider) => {
          return provider?.id === currentProvider?.provider?.id;
        });
        if (exists) {
          temp = { ...exists };
        } else {
          const tempId = "temp_" + index;
          temp = { provider, username: "", id: tempId };
        }
        return temp;
      }).filter((el) => el != null);
    }
    return null;
  }, [state?.user]);

  const saveProviders = (prev, data) => {
    const mappedData = {
      ...data,
      user: state.user.id,
      id: data?.id?.toString().includes("temp_") ? null : data.id,
    };
    mainAxios
      .post("/apiEndpoint/saveOrUpdate", {
        object: "UserAuthProviders",
        content: { ...mappedData },
        nuis: loggedInUser.companiesRights.find(
          (company) => company.id === parseInt(selectedCompany)
        )?.nuis,
      })
      .then((res) => {
        if (res.data.status) {
          getCurrentUser();
          toast.success("Auth Provider saved successfully!", {
            containerId: "all_toast",
          });
        }
      });
  };

  const isGoogleConnected = state?.user?.userProviders?.find(
    (up) => up.username?.length > 0 && up?.provider?.name?.toUpperCase() === "GOOGLE"
  );
  const isMicrosoftConnected = state?.user?.userProviders?.find(
    (up) => up.username?.length > 0 && up?.provider?.name?.toUpperCase() === "MICROSOFT"
  );

  const googleState = useMemo(
    () =>
      encodeURIComponent(
        JSON.stringify({
          userId: state.user.id,
          provider: "Google",
          action: isGoogleConnected ? "unlink" : "link",
          pathname: location.pathname,
        })
      ),
    [state.user, isGoogleConnected, location.pathname]
  );

  const microsoftState = useMemo(
    () =>
      encodeURIComponent(
        JSON.stringify({
          userId: state.user.id,
          provider: "Microsoft",
          action: isMicrosoftConnected ? "unlink" : "link",
          pathname: location.pathname,
          nonce: microsoftNonce, 
          verifier:microsoftCodeVerifier?.verifier
        })
      ),
    [state.user, isMicrosoftConnected, location.pathname, microsoftNonce]
  );

  const redirectUri = `${window.location.origin}`;
  const googleUrl = `https://accounts.google.com/o/oauth2/v2/auth?response_type=code&redirect_uri=${redirectUri}&client_id=${process.env.REACT_APP_GOOGLE_CLIENT_ID}&scope=openid%20email%20profile&access_type=offline&prompt=consent&state=${googleState}`;
  const microsoftUrl = useMemo(() => {
    if (!microsoftCodeVerifier || !microsoftNonce) return "#";
    return `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${process.env.REACT_APP_MICROSOFT_CLIENT_ID}&response_type=code&redirect_uri=${encodeURIComponent(redirectUri)}&response_mode=query&scope=openid%20profile%20email%20offline_access%20User.Read&state=${microsoftState}&nonce=${microsoftNonce}&code_challenge=${microsoftCodeVerifier.challenge}&code_challenge_method=S256`;
  }, [microsoftCodeVerifier, microsoftNonce, microsoftState, redirectUri]);

  const customAction = ({ row, isEditing, edit, save, cancel }) => {
    const name = row?.provider?.name;

    if (name === "Google") {
      return (
        <button
          className="text-primary btn btn-link p-0"
          style={{ whiteSpace: 'nowrap' }}
          disabled={providerDisabled}
          onClick={() => {
            if (isGoogleConnected) {
              unlinkProvider({
                provider: "Google",
                recordUser: loggedInUser.username,
                userId:state.user.id,
                onSuccess: (message) => {
                  toast.success(message, {
                    containerId: "all_toast",
                  });
                  getCurrentUser()}
              });
            } else {
              window.location.href = googleUrl;
            }
          }}
        >
          {isGoogleConnected ? "Unlink" : "Link"} {name}
        </button>
      );
    }
    if (name === "Microsoft") {
      return (
        <button
          className="text-primary btn btn-link p-0"
          style={{ whiteSpace: 'nowrap' }}
          disabled={providerDisabled}
          onClick={() => {
            if (isMicrosoftConnected) {
              unlinkProvider({
                provider: "Microsoft",
                recordUser: state.user.username,
                userId:state.user.id,
                onSuccess: () =>{
                  toast.success(t("users.userUnlinkedSuccessfully", { provider:"Microsoft" }), {
                    containerId: "all_toast",
                  });
                  getCurrentUser()
                  },
              });
            } else {
              if (microsoftCodeVerifier && microsoftNonce) {
                localStorage.setItem("microsoft_code_verifier", microsoftCodeVerifier?.verifier);
                localStorage.setItem("microsoft_nonce", microsoftNonce);
                window.location.href = microsoftUrl;
              } else {
                toast.error("Authentication setup is not complete. Please try again.", {
                  containerId: "all_toast",
                });
              }
            }
          }}
        >
          {isMicrosoftConnected ? "Unlink" : "Link"} {name}
        </button>
      );
    }
    if (isEditing) {
      return (
        <div>
          <button
            type="button"
            className="btn btn-primary p-1 mr-2"
            disabled={providerDisabled}
            onClick={() => save(row)}
          >
            <FontAwesomeIcon icon={faCheck} className="mr-2" />
            {t("common.save")}
          </button>
          <button
            type="button"
            style={{ padding: "0.2em" }}
            className="btn btn-secondary"
            onClick={cancel}
          >
            {t("common.cancel")}
          </button>
        </div>
      );
    }
    return (
      <div type="button" onClick={() =>!providerDisabled && edit(row)} className={`text-primary ${providerDisabled? " text-muted":""}`} style={{cursor: providerDisabled ? "unset" : "pointer"}}>
        <FontAwesomeIcon icon={faEdit} className="mr-1" />
        {t("common.edit")}
      </div>
    );
  };

  return (
    <div className="card filter-card-dashboard shadow mb-4">
      <div className="card-title p-3 ml-2 text-left d-flex align-items-center">
        <h3 className="mb-0">Authentication Providers</h3>
      </div>
      <div className="card-body">
        {tableData && (
          <EditableTable
            columns={columns}
            data={tableData ? [...tableData] : []}
            getEditedData={saveProviders}
            customAction={customAction}
            columnDeps={[state.user]}
          />
        )}
      </div>
    </div>
  );
};

export default AuthenticationProviders;